import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, uniq } from 'lodash';
import { dollarAmount } from 'src/common/display/Money/DollarAmount';
import getRemainingAuthorizedAmountPerInvoice from 'actions/Contact/Group/getRemainingAuthorizedAmountPerInvoice';
import { connect } from 'react-redux';
import WorkqueueBulkAction from 'common/Workqueue/WorkqueueBulkAction';
import { useBulkUpdateInvoiceStatus } from '../../hooks/useUpdateInvoice';
import { useFind } from '../../../../api/APIHooks';
import { InvoiceBulkActionsModalRows } from './InvoiceBulkActionsModalRows';
import {
  getHasInvoicesAboveRemainingAuth,
  getCanBulkActionAllInvoices,
  getErroredInvoiceIds,
} from '../../utils/bulkActionAuthUtils';

const BulkPayInvoices = ({
  deselectAll,
  deselectRows,
  selectedRows,
  invoiceIds,
  loadingInvoicedSpends,
  invoicedSpends,
}) => {
  const iwqBulkPayFormRef = useRef();
  const disableBulkPayButton = !(selectedRows.length > 0 &&
    selectedRows.every((i) => i.invoice_status === 'accepted_by_payer'));

  const closeBulkPayForm = () => {
    iwqBulkPayFormRef.current.closeDialog();
  };

  const openBulkPayForm = () => {
    iwqBulkPayFormRef.current.openDialog();
  };

  const bulkPayInvoices = useBulkUpdateInvoiceStatus(
    invoiceIds,
    'paid',
  );

  const totalBulkInvoicesAmount = !isEmpty(selectedRows) ?
    dollarAmount(selectedRows.reduce((acc, invoice) => acc + invoice?.total_amount_invoiced, 0), true) : 0;

   const submitBulkPaidInvoices = async () => {
    await bulkPayInvoices();
    closeBulkPayForm();
    deselectAll();
  };

  const fspIds = uniq(selectedRows.map((invoice) => invoice.fee_schedule_program_id));
  const { isSuccess: isFSPsLoaded, data } = useFind(
    'fee_schedule_programs',
    { id: fspIds.join() },
    { queryConfig: { placeholderData: undefined } },
  );

  const fsps = get(data, 'data.data', []);
  const invoiceFsps = Object.fromEntries(selectedRows.map((invoice) => [
    invoice.id, fsps.find((fsp) => invoice.fee_schedule_program_id === fsp.id),
  ]));

  const hasInvoicesAboveRemainingAuthorizedAmount =
    getHasInvoicesAboveRemainingAuth(invoicedSpends);
  const canPayAllInvoices =
    getCanBulkActionAllInvoices(selectedRows, invoicedSpends, invoiceFsps);
  const erroredInvoiceIds =
    getErroredInvoiceIds(selectedRows, invoicedSpends, invoiceFsps);

  const invoicesNotAuthorizedToSubmit = selectedRows.length === 0 ||
    (hasInvoicesAboveRemainingAuthorizedAmount && !canPayAllInvoices) ||
    loadingInvoicedSpends || !isFSPsLoaded;

  return (
    <WorkqueueBulkAction
      action={'Pay'}
      open={openBulkPayForm}
      close={closeBulkPayForm}
      onSubmit={submitBulkPaidInvoices}
      bulkActionIcon="LogoShield"
      dialogTitle="Pay Selected Invoices"
      confirmLabel="Pay"
      dialogRef={iwqBulkPayFormRef}
      disableOpenModal={disableBulkPayButton}
      selectedRows={selectedRows}
      deselectAll={deselectAll}
      disableSubmitRowsButton={invoicesNotAuthorizedToSubmit}
    >
      <>
        <p className="mx-3 my-4">
          You are about to mark the following invoices as
          <strong> paid </strong>for a total of{' '}
          <strong>{totalBulkInvoicesAmount}</strong>. Would you like to continue?
        </p>
        <div className="my-6">
          <InvoiceBulkActionsModalRows
            invoices={selectedRows}
            deselectRows={deselectRows}
            erroredInvoiceIds={erroredInvoiceIds}
          />
          {hasInvoicesAboveRemainingAuthorizedAmount && (
          <p className="text-red text-13px pl-5 mt-5">
            {canPayAllInvoices ?
              'The remaining authorized amount is less than the invoice amount for one or more invoices.' :
              'One or more of the invoices cannot be accepted because the remaining authorized amount is ' +
              'less than the invoice amount.'}
          </p>
        )}
        </div>
      </>
    </WorkqueueBulkAction>
  );
};

BulkPayInvoices.propTypes = {
  deselectAll: PropTypes.func.isRequired,
  deselectRows: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  invoiceIds: PropTypes.array.isRequired,
  invoicedSpends: PropTypes.object.isRequired,
  loadingInvoicedSpends: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  const networkId = state.networks.networkId;
  return {
    networkId,
    invoicedSpends: get(state, 'invoicedSpend.invoicedSpends'),
    loadingInvoicedSpends: get(state, 'invoicedSpend.isFetching'),
  };
}

export { BulkPayInvoices };
export default connect(
mapStateToProps,
  { getRemainingAuthorizedAmountPerInvoice },
)(
  BulkPayInvoices,
);
